import { Role, rolesManipulator } from 'auth';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useCallback, useContext, useMemo, useState } from 'react';
import { FiltersGroup } from '@/modules/common/components/FiltersGroup.tsx';
import IssuerCorrespondenceItemModal from '@/modules/issuer-dashboard/components/modals/IssuerCorrespondenceItemModal.tsx';
import {
  useIssuerCommunicationsListQuery,
  useReprocessCorrespondenceMutation,
} from '@/modules/issuer-dashboard/queries/communications.ts';
import {
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  SearchField,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { DotsHorizontalIcon } from 'ui/icons';
import {
  CommunicationType,
  CorrespondenceCommunicationActivityItem,
  GetCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGetParams,
} from 'api';

const baseHeadItems: TableHeadColumn[] = [
  { title: 'Subject', className: 'min-w-[250px] !pr-3' },
  { title: 'Sender', className: '!px-3' },
  { title: 'Date & Time', className: 'min-w-[150px] !px-3' },
  { title: 'Type', className: '!px-3' },
  { title: 'Recipients', className: '!px-3 w-40' },
];

type Filters = Omit<
  GetCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGetParams,
  'page_size' | 'page' | 'portfolio_id' | 'type'
>;

interface PortfolioSourceEmailsTabProps {
  portfolioId: number;
}

export const PortfolioSourceEmailsTab = ({ portfolioId }: PortfolioSourceEmailsTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const [filters, setFilters] = useState<Filters>({});
  const [reprocessingId, setReprocessingId] = useState<number | null>(null);
  const isIssuerAdmin = rolesManipulator.current_user_role === Role.SR_ISSUER_ADMIN;
  const { mutate: reprocessCorrespondence } = useReprocessCorrespondenceMutation();
  const { data, isLoading, fetchNextPage, hasNextPage } = useIssuerCommunicationsListQuery({
    page: 1,
    page_size: 15,
    portfolio_id: portfolioId,
    type: CommunicationType.External,
    ...filters,
  });

  const handleOpenCorrespondenceItemModal = useCallback(
    (item: CorrespondenceCommunicationActivityItem) => {
      openModal(IssuerCorrespondenceItemModal, { item });
    },
    [openModal],
  );

  const handleReprocessCorrespondence = useCallback(
    (correspondenceId: number) => {
      setReprocessingId(correspondenceId);
      reprocessCorrespondence(correspondenceId, {
        onSettled: () => setReprocessingId(null),
      });
    },
    [reprocessCorrespondence],
  );

  const headItems = useMemo<TableHeadColumn[]>(
    () =>
      isIssuerAdmin ? [...baseHeadItems, { title: '', className: 'w-12 !px-3' }] : baseHeadItems,
    [isIssuerAdmin],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    return (data?.pages || []).flatMap(({ data }) =>
      data.map(item => {
        const cells = [
          { cell: <div className="truncate max-w-[40vw]">{item.subject}</div>, className: '!pr-3' },
          {
            cell: (
              <div className="max-w-[150px]">
                {item.sender_name && <div className="!text-sm truncate">{item.sender_name}</div>}
                {item.sender_email && (
                  <div className="text-gray-600 truncate">{item.sender_email}</div>
                )}
              </div>
            ),
            className: '!px-3',
          },
          { cell: dayjs(item.created_at).format('DD-MM-YYYY, HH:mm'), className: '!px-3' },
          { cell: <div className="capitalize">{item.type}</div>, className: '!px-3' },
          { cell: 'Sent to register', className: '!px-3' },
        ];

        if (isIssuerAdmin) {
          cells.push({
            cell: (
              <SourceEmailMoreButton
                item={item}
                isLoading={reprocessingId === item.id}
                onReprocess={handleReprocessCorrespondence}
              />
            ),
            className: '!px-3',
          });
        }

        return {
          onClick: () => handleOpenCorrespondenceItemModal(item),
          cells,
        };
      }),
    );
  }, [
    data?.pages,
    handleOpenCorrespondenceItemModal,
    handleReprocessCorrespondence,
    isIssuerAdmin,
    reprocessingId,
  ]);

  const handleChangeSearchFilter = useCallback((text: string) => {
    setFilters(current => (text ? { ...current, text } : omit(current, 'text')));
  }, []);

  const handleChangeSenderFilter = useCallback((sender: string) => {
    setFilters(current => (sender ? { ...current, sender } : omit(current, 'sender')));
  }, []);

  return (
    <div className="flex flex-col gap-2 w-full">
      <FiltersGroup className="px-3 sm:px-6 flex flex-wrap items-center gap-2">
        <SearchField
          allowClear
          className="max-w-[440px]"
          placeholder="Search by email text"
          onChange={handleChangeSearchFilter}
        />
        <SearchField
          allowClear
          className="max-w-[440px]"
          placeholder="Search by sender name or email"
          onChange={handleChangeSenderFilter}
        />
      </FiltersGroup>
      <Table
        blockIdInfinite="scrollable"
        isSearchUsed={!isEmpty(filters)}
        isLoading={isLoading}
        fetchNextPageInfinite={fetchNextPage}
        hasNextPageInfinite={hasNextPage}
        withRounded={false}
        withBorder={false}
        withBorderX={false}
        className="my-0 [&_th]:text-xs [&_td:not(.absolute)]:text-xs [&_td.absolute]:!top-[-60px]"
        items={bodyItems}
        headItems={headItems}
      />
    </div>
  );
};

interface SourceEmailMoreButtonProps {
  item: CorrespondenceCommunicationActivityItem;
  isLoading: boolean;
  onReprocess: (correspondenceId: number) => void;
}

const SourceEmailMoreButton = ({ item, isLoading, onReprocess }: SourceEmailMoreButtonProps) => {
  const items = useMemo<DropdownMenuItem[]>(
    () => [
      {
        label: 'Rerun email',
        onClick: () => onReprocess(item.id),
      },
    ],
    [item.id, onReprocess],
  );

  return (
    <DropdownMenu
      items={items}
      disabled={isLoading}
      button={
        <button className="flex items-center justify-center" aria-label="Email actions">
          <img
            alt="More..."
            src={DotsHorizontalIcon}
            className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
          />
        </button>
      }
      placement="left-start"
      autoPlace
      containerClassName="max-w-6"
    />
  );
};
